import { useMemo } from "react";
import {
  normalizeCoords,
  satelliteImageUrl,
  streetViewImageUrl,
} from "../Utils/googleMapsImageUrls";
import { useStreetViewAvailability } from "./useStreetViewAvailability";

export interface GoogleSlide {
  type: "satellite" | "streetview";
  url: string;
  label: string;
}

/**
 * Builds up to two Google-derived slides for a property's photo gallery.
 *
 * Always includes Satellite when the coords are valid (Static Maps has
 * imagery for every coordinate on earth at residential zoom). Includes
 * Street View only when the free metadata endpoint confirms a panorama
 * exists nearby AND the owner has not opted the slide out (some panos
 * are over-blurred / privacy-masked and the owner is the only person
 * who can look at it and decide it's useless).
 *
 * Accepts any input shape — raw numbers, numeric strings, undefined,
 * NaN. `normalizeCoords` does the validation.
 *
 * While the Street View metadata check is in flight, the hook returns
 * just the Satellite slide. Once metadata resolves, the Street View
 * slide either appears or is left off.
 */
export interface UseGoogleSlidesOptions {
  /** Owner-controlled — hide the Street View slide for this property. */
  hideStreetView?: boolean;
}

export const useGoogleSlides = (
  rawLat: unknown,
  rawLng: unknown,
  options: UseGoogleSlidesOptions = {}
): GoogleSlide[] => {
  const coords = useMemo(
    () => normalizeCoords(rawLat, rawLng),
    [rawLat, rawLng]
  );
  const streetViewAvailable = useStreetViewAvailability(
    coords?.lat,
    coords?.lng
  );
  const { hideStreetView = false } = options;

  return useMemo<GoogleSlide[]>(() => {
    if (!coords) return [];
    const slides: GoogleSlide[] = [
      {
        type: "satellite",
        url: satelliteImageUrl(coords.lat, coords.lng),
        label: "Satellite View",
      },
    ];
    // Only include the Street View slide when metadata confirms an
    // OUTDOOR panorama nearby (streetViewAvailable === false covers
    // both "no pano" and rare indoor-only areas) AND the owner hasn't
    // explicitly opted out. Pass the computed heading so the camera
    // faces the property from wherever the pano actually sits.
    if (!hideStreetView && streetViewAvailable && streetViewAvailable.ok) {
      slides.push({
        type: "streetview",
        url: streetViewImageUrl(
          coords.lat,
          coords.lng,
          undefined,
          undefined,
          undefined,
          streetViewAvailable.heading
        ),
        label: "Street View",
      });
    }
    return slides;
  }, [coords, streetViewAvailable, hideStreetView]);
};
